import * as React from "react";
import { Building2, LogOut, Monitor, Moon, Search, Sun } from "lucide-react";
import { useNavigate } from "react-router";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TenantSwitcher } from "@/components/layout/TenantSwitcher";
import { personaLabel } from "@/components/layout/nav";
import { cn } from "@/lib/cn";
import { Kbd, modKey, shellOverlays } from "@/lib/shortcuts";
import { useAuthStore } from "@/store/authStore";
import { useMe } from "@/api/me";
import { useViewAsCandidates } from "@/api/users";
import { useStartViewAs, useStopViewAs } from "@/lib/impersonation";
import { useUIStore, type ThemePreference } from "@/store/uiStore";
import { APP_VERSION, BUILD_TIME, isMobileBuild } from "@/platform";

/**
 * Top bar (MV2-845, app/Main · ShellOM · ShellClientAdmin · ShellSuperAdmin).
 *
 * 52px on `--surface`: context chip (the tenant, or the switcher for a
 * SuperAdmin) · "Search or jump · ⌘K" · spacer · persona badge · theme ·
 * bell · avatar. The acting-as banner sits above it (ImpersonationBanner).
 * Desktop and tablet only — MobileFrame owns the 48px compact bar.
 */
export function Header(): React.ReactElement {
  const { data: me } = useMe();
  const acting = useAuthStore((s) => s.actingAs?.clientId != null);
  const persona = me?.primary_persona ?? null;
  const effective =
    persona === "SuperAdmin" && acting ? "ClientAdmin" : persona;

  return (
    <header className="sticky top-0 z-30 flex h-[52px] items-center gap-3 border-b border-gray-200 bg-surface px-[18px]">
      {persona === "SuperAdmin" ? (
        <TenantSwitcher />
      ) : (
        <span className={cn(CHIP, "text-gray-700")} data-testid="context-chip">
          <Building2 size={13} aria-hidden />
          <span className="max-w-[220px] truncate">
            {me?.client?.name ?? "…"}
          </span>
        </span>
      )}
      <button
        type="button"
        onClick={shellOverlays.openPalette}
        aria-label={`Search or jump (${modKey("K")})`}
        className={cn(
          CHIP,
          "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
        )}
      >
        <Search size={13} aria-hidden />
        <span>Search or jump ·</span>
        <Kbd>{modKey("K")}</Kbd>
      </button>

      <span className="flex-1" />

      {effective && (
        <span className="inline-flex h-[22px] items-center rounded-[5px] bg-gray-100 px-[7px] text-[10.5px] font-semibold text-gray-600">
          {personaLabel(effective)}
        </span>
      )}
      <ThemeCycle />
      <NotificationsBell />
      <UserMenu />
    </header>
  );
}

const CHIP =
  "inline-flex h-8 items-center gap-[7px] rounded-full border border-gray-200 bg-surface px-3 text-[12.5px] transition-colors duration-hover";

const THEME_ORDER: ThemePreference[] = ["light", "system", "dark"];
const THEME_ICON: Record<
  ThemePreference,
  React.ComponentType<{ size?: number }>
> = {
  light: Sun,
  system: Monitor,
  dark: Moon,
};

/** One icon in the bar; the full choice lives in the user menu. */
function ThemeCycle(): React.ReactElement {
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const Icon = THEME_ICON[theme];
  const next =
    THEME_ORDER[(THEME_ORDER.indexOf(theme) + 1) % THEME_ORDER.length]!;
  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      title="Theme · light / system / dark"
      aria-label={`Theme: ${theme}. Switch to ${next}`}
      className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors duration-hover hover:bg-gray-100 hover:text-gray-900"
    >
      <Icon size={16} />
    </button>
  );
}

// --------------------------------------------------------------------------- //
// Desktop user menu (ops/UserMenu) — identity · theme · view as · settings ·
// help · shortcuts · sign out · build
// --------------------------------------------------------------------------- //

/** The help line under "View as" — desktop menu and mobile sheet. */
export const VIEW_AS_HELP =
  "Shows the app as that user sees it: their role, policies and sites. Read-only while on.";

/**
 * MV2-1250 — "View as" a real user of the Acting-as client (backend.md
 * §108). Gated on the Cognito SuperAdmin group, not `/me`, because `/me`
 * answers as the viewed user while this is on. Shared with the mobile
 * account sheet (MobileFrame.tsx).
 */
export function ViewAsSelect({
  id,
  className,
  onPicked,
}: {
  id: string;
  className: string;
  onPicked?: () => void;
}): React.ReactElement {
  const actingAs = useAuthStore((s) => s.actingAs);
  const viewingAs = useAuthStore((s) => s.viewingAs);
  const startViewAs = useStartViewAs();
  const stopViewAs = useStopViewAs();
  const users = useViewAsCandidates(true);
  const rows = users.data ?? [];

  const reason = !actingAs
    ? "Pick a client in Acting as first — View as lists that client's users."
    : users.isError
      ? "Couldn't load this client's users. Reopen the menu to retry."
      : users.isLoading
        ? "Loading this client's users…"
        : rows.length === 0
          ? `${actingAs.clientName} has no users to view as yet.`
          : undefined;

  const pick = (userId: string) => {
    const u = rows.find((r) => r.id === userId);
    if (u && actingAs) {
      // Lands on the viewed user's own home (DashboardRouter picks it).
      startViewAs({
        userId: u.id,
        name: u.full_name,
        persona:
          u.persona_class ??
          (u.cognito_group === "ClientAdmin" ? "ClientAdmin" : null),
        clientId: actingAs.clientId,
        clientName: actingAs.clientName,
      });
    } else {
      stopViewAs();
    }
    onPicked?.();
  };

  return (
    <select
      id={id}
      value={viewingAs?.userId ?? ""}
      onChange={(e) => pick(e.target.value)}
      disabled={reason !== undefined && !viewingAs}
      title={reason}
      className={cn(className, "max-w-[180px] disabled:opacity-60")}
    >
      <option value="">Myself · SuperAdmin</option>
      {viewingAs && !rows.some((u) => u.id === viewingAs.userId) && (
        <option value={viewingAs.userId}>{viewingAs.name}</option>
      )}
      {rows.map((u) => {
        const role = personaLabel(
          u.persona_class ??
            (u.cognito_group === "ClientAdmin" ? "ClientAdmin" : null),
        );
        return (
          <option key={u.id} value={u.id}>
            {role ? `${u.full_name} · ${role}` : u.full_name}
          </option>
        );
      })}
    </select>
  );
}

function formatBuilt(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function UserMenu(): React.ReactElement {
  const navigate = useNavigate();
  const authEmail = useAuthStore((s) => s.email);
  const signOutAll = useAuthStore((s) => s.signOutAll);
  const actingAs = useAuthStore((s) => s.actingAs);
  const isSuperAdmin = useAuthStore((s) => s.groups.includes("SuperAdmin"));
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const { data: me } = useMe();

  const name = me?.full_name ?? authEmail ?? "Account";
  const email = me?.email ?? authEmail ?? "";
  const persona = me?.primary_persona ?? null;
  const initial = (name || email || "?").slice(0, 1).toUpperCase();
  const avatarUrl = me?.avatar_url ?? null;
  const where =
    persona === "SuperAdmin" && !actingAs
      ? "re:bot platform"
      : (actingAs?.clientName ?? me?.client?.name ?? "");
  const built = formatBuilt(BUILD_TIME);

  async function handleSignOut() {
    try {
      await signOutAll();
    } catch (err) {
      console.error("signOut failed", err);
    }
    window.location.assign("/login");
  }

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-[color:var(--brand-primary)] text-[11px] font-semibold text-[color:var(--brand-primary-fg)] ring-2 ring-transparent transition-shadow duration-hover hover:ring-[color:var(--focus)]/40"
          aria-label="Account menu"
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            initial
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[300px] overflow-hidden p-0"
      >
        <div className="flex items-center gap-[11px] border-b border-gray-100 px-3.5 pb-3 pt-3.5">
          <span className="flex h-7 w-7 flex-none items-center justify-center overflow-hidden rounded-full bg-[color:var(--brand-primary)] text-[11px] font-semibold text-[color:var(--brand-primary-fg)]">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              initial
            )}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-px">
            <span className="truncate text-[13px] font-semibold text-gray-900">
              {name}
            </span>
            {email && <span className="type-meta truncate">{email}</span>}
            <div className="mt-[3px] flex items-center gap-1.5">
              {persona && (
                <span className="inline-flex h-[18px] items-center rounded-[5px] bg-[color:color-mix(in_srgb,var(--action)_13%,transparent)] px-[7px] text-[10.5px] font-semibold text-[color:var(--action)]">
                  {personaLabel(persona)}
                </span>
              )}
              {where && <span className="type-meta truncate">{where}</span>}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 border-b border-gray-100 px-3.5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-medium text-gray-800">Theme</span>
            <span className="flex-1" />
            <div
              role="radiogroup"
              aria-label="Theme"
              className="inline-flex gap-0.5 rounded-[8px] border border-gray-200 bg-surface-sunk p-0.5"
              onKeyDown={stop}
            >
              {THEME_ORDER.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={theme === t}
                  onClick={() => setTheme(t)}
                  className={cn(
                    "rounded-[6px] px-2.5 py-1 text-[12px] capitalize",
                    theme === t
                      ? "bg-surface font-medium text-gray-900 shadow-xs"
                      : "text-gray-600",
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          {isSuperAdmin && (
            <>
              <div className="flex items-center gap-2">
                <label
                  htmlFor="view-as"
                  className="text-[12px] font-medium text-gray-800"
                >
                  View as
                </label>
                <span className="flex-1" />
                <span onKeyDown={stop} onPointerDown={stop}>
                  <ViewAsSelect
                    id="view-as"
                    className="h-7 rounded-[6px] border border-gray-200 bg-surface px-2 text-[12px] text-gray-900"
                  />
                </span>
              </div>
              <span className="type-meta">{VIEW_AS_HELP}</span>
            </>
          )}
        </div>

        <div className="p-1.5">
          <MenuRow
            label="Settings"
            sub="profile · notifications · security"
            onSelect={() => navigate("/settings/my-profile")}
          />
          <MenuRow
            label="Help and tours"
            sub="replay any persona tour"
            onSelect={() => navigate("/getting-started")}
          />
          <MenuRow
            label="Keyboard shortcuts"
            sub="?"
            onSelect={shellOverlays.openShortcuts}
          />
          <MenuRow
            label="Sign out"
            danger
            onSelect={() => void handleSignOut()}
          />
        </div>

        <div className="type-meta tabular border-t border-gray-100 px-3.5 py-2">
          v{APP_VERSION} · {isMobileBuild ? "mobile" : "web"}
          {built && ` · built ${built}`}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MenuRow({
  label,
  sub,
  danger,
  onSelect,
}: {
  label: string;
  sub?: string;
  danger?: boolean;
  onSelect: () => void;
}): React.ReactElement {
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      className={cn(
        "flex items-center gap-[9px] rounded-[7px] px-[9px] py-2",
        danger && "text-[color:var(--danger)]",
      )}
    >
      {danger && <LogOut size={13} aria-hidden />}
      <span className="flex-1 text-[12.5px]">{label}</span>
      {sub && <span className="type-meta">{sub}</span>}
    </DropdownMenuItem>
  );
}
